import { Check, X, ArrowDown } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { GoldButton, GhostButton } from "@/components/Buttons";
import { Eyebrow } from "@/components/PageHero";
import { PaperPlane } from "@/components/PaperPlane";
import VideoHero from "@/components/VideoHero";
import InkDivider from "@/components/InkDivider";
import Packages from "@/components/Packages";
import VideoTestimonials from "@/components/VideoTestimonials";
import { CALENDAR_URL } from "@/lib/site";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Prolific Maven",
  description:
    "Meta ads management and brand voice strategy agency serving local businesses in Bradenton, Sarasota, Tampa, and surrounding areas of Florida.",
  url: "https://prolificmaven.com",
  areaServed: [
    "Parrish, FL", "Bradenton, FL", "Sarasota, FL", "Tampa, FL", "St. Petersburg, FL",
    "Lakeland, FL", "Palmetto, FL", "Ellenton, FL", "Manatee County, FL",
    "Hillsborough County, FL", "Sarasota County, FL", "Pinellas County, FL",
  ],
  serviceType: ["Meta Ads Management", "Facebook Advertising", "Brand Voice Strategy", "Video Ad Production"],
  address: { "@type": "PostalAddress", addressLocality: "Parrish", addressRegion: "FL", addressCountry: "US" },
};

// The three-step framework: simple title, real specifics underneath
const framework = [
  {
    step: "01",
    label: "Clarify",
    t: "First, figure out who you are and who you're talking to.",
    d: "Most businesses skip this and it costs them everything. Before a single ad is written, we sit down and map out exactly who your best customer is: their age, their situation, what keeps them up at night, what they've already tried, and why they chose you over someone else. We call this your Ideal Client Profile (ICP). We also get clear on what makes you different. Not 'great service' or 'years of experience.' The real thing. The specific reason someone should choose you over the three competitors they're also looking at. Without this clarity, every ad you run is a guess. With it, every ad has a target.",
  },
  {
    step: "02",
    label: "Refine",
    t: "Then, find the right words and help you say them.",
    d: "Once we know who we're talking to, we research what actually moves them. We study the exact language your ideal customer uses when they're frustrated, searching, or ready to buy. We build your ad scripts around those words. Then we show up in person and coach you through delivering them on camera. Not a polished, corporate performance. A real, believable conversation between you and the person watching. Because people don't buy from logos. They buy from people they trust. The goal of this phase is to make sure that when your ideal customer sees your face and hears your voice, they feel like you're speaking directly to them.",
  },
  {
    step: "03",
    label: "Amplify",
    t: "Then, and only then, we run the ads.",
    d: "This is where most agencies start. It's also why most campaigns fail. By the time we turn on ad spend, we already know who we're targeting, what message stops their scroll, and what offer makes them reach out. We build your Meta campaigns from scratch: pixel installation, account structure, audience targeting, campaign objective, budget allocation. Every dollar goes toward conversion. Not likes, not views, not brand awareness. We target your local market (or national, if that fits your business), optimize continuously based on real data, and send you a clear monthly report showing exactly what's working and what we're adjusting. And when an ad starts to get stale, because they all do eventually, we rotate in fresh creative before performance drops.",
  },
];

const villains = [
  {
    t: "Boosting posts",
    d: "Hitting 'Boost' on a Facebook post is not running ads. It's paying Meta to show your post to more people with no targeting, no conversion goal, and no strategy. The people who see it weren't looking for you. They weren't even close to buying. You paid to interrupt strangers, not reach customers.",
  },
  {
    t: "Hiring someone to 'manage your social media'",
    d: "Posting content every day keeps your page active. But organic reach on Facebook and Instagram is between 2–5%. That means if you have 1,000 followers, roughly 20–50 people see each post. You're not building a pipeline. You're maintaining a digital brochure that almost no one reads.",
  },
  {
    t: "Shared lead platforms like Angi, HomeAdvisor, or Thumbtack",
    d: "These platforms sell the same lead to multiple businesses. By the time that person reaches you, they've already heard from two or three of your competitors. You're not their first call. You're a price comparison. The leads are cheap because they're not really yours.",
  },
];

const phases = [
  {
    n: "01",
    t: "We learn your business and your best customer.",
    d: "We start with a deep discovery session, not an intake form. A real conversation. We want to know who your best clients are, what they said when they decided to hire you, what they were worried about before they called, and what made them choose you over everyone else. This becomes the foundation of every ad we write. Technically: we're building your Ideal Client Profile (ICP), defining your positioning, and mapping your offer to the awareness level of your audience.",
  },
  {
    n: "02",
    t: "We test what actually stops the scroll, before spending real money on video.",
    d: "Before we put you on camera, we run 18 low-cost image-based ads to find which messages make your ideal customer stop and pay attention. Think of it as a small, cheap experiment that tells us exactly what to say in your videos. This saves thousands of dollars in wasted creative. Technically: we're running a hook-testing phase using static creative in a Meta Sales campaign, optimizing for link clicks, to identify winning angles before video production.",
  },
  {
    n: "03",
    t: "We write your scripts and coach you through filming.",
    d: "Once we know which messages win, we write ad scripts built around them: four angles that meet your buyer wherever they are, whether they've never heard of you or they've been thinking about calling for months. Then we show up and coach you through delivering them. You don't need to be a natural on camera. You just need to be you. Technically: we're producing UGC-style (user-generated content) video creative across four awareness stages: problem-aware, solution-aware, product-aware, and most-aware, to ensure full-funnel coverage.",
  },
  {
    n: "04",
    t: "We build and launch your campaigns the right way.",
    d: "We set up your Meta ad account from scratch, or clean up what's already there. Pixel installed and verified. Campaign built for conversions, not vanity metrics. Prospecting campaigns reach people who've never heard of you. Retargeting campaigns follow up with people who have. The algorithm learns who your buyers are and finds more of them over time. Technically: CBO (Campaign Budget Optimization) structure, Advantage+ placements, Sales objective, custom and lookalike audiences, with CAPI (Conversions API) for server-side tracking.",
  },
  {
    n: "05",
    t: "New leads hear from you before they call anyone else.",
    d: "Speed wins. When someone fills out a form or sends a message, they're in a window, maybe 5 minutes wide, where they're ready to talk. If you're not first, someone else is. We build an automatic follow-up system that sends a text and a message the moment a lead comes in, followed by a sequence that nurtures them for weeks if they don't respond right away. Technically: automated SMS + Messenger follow-up via CRM integration, a 14-day no-response sequence, and a 90-day reactivation sequence for cold leads.",
  },
  {
    n: "06",
    t: "We keep the ads fresh so they keep performing.",
    d: "Every ad has a lifespan. Once your audience has seen the same ad enough times, they start to ignore it. Cost-per-lead climbs and results drop. We track this and rotate in new creative before it becomes a problem. You don't have to think about it. Technically: creative fatigue monitoring via frequency and CPL trends, with pre-scheduled creative refreshes in sets of three to maintain algorithmic momentum.",
  },
];

const fits = [
  "You sell something people think about before they buy: a service, a renovation, a big decision",
  "You're tired of waiting on referrals and want a predictable way to get new clients",
  "You're willing to show up on camera, because your face and your story are the most powerful ad you have",
  "You have at least $1,500/month to put into ad spend, paid directly to Meta and not through us",
  "You respond to leads quickly, because the business that answers first usually wins",
  "You want to be the name people think of first in your market, not just another option",
];

const notFits = [
  "You want to test it for a month and see what happens",
  "You're not willing to appear on camera",
  "You don't have a system for following up with leads fast",
  "You're looking for the lowest price, not the best outcome",
];

const objections = [
  {
    q: "\"I've tried Facebook Ads before and it didn't work.\"",
    a: "This is the most common thing we hear, and almost every time, the campaign failed for one of three reasons: it ran an Engagement or Traffic campaign instead of a Conversion campaign (so Meta was optimizing for clicks, not customers), it skipped the hook-testing phase and went straight to video (so the message was a guess), or the ad said what the business wanted to say instead of what the customer needed to hear. Same platform. Completely different approach.",
  },
  {
    q: "\"I don't know what to say on camera.\"",
    a: "That's exactly what the Refine phase is for. We write the script. We coach you through it. We're not looking for a polished performance. We're looking for you talking directly to the person who needs what you offer. The best-performing ads don't look like commercials. They look like someone's phone video. We've coached business owners who had never been on camera in their lives. They all got through it. So will you.",
  },
  {
    q: "\"Why does my ad spend go directly to Meta instead of through you?\"",
    a: "Because everything your campaigns learn belongs to you. Every conversion, every audience, every data point your Meta pixel collects is a business asset. When an agency runs your ads through their own account, all of that data lives with them. When you leave, it disappears. We work inside your account, on your pixel, with your data. If we ever part ways, you keep everything your campaigns have learned.",
  },
  {
    q: "\"How long before I see results?\"",
    a: "Most clients start seeing leads within the first 30 days of launch. The hook-testing phase in weeks one and two starts generating real data immediately. By the time video ads go live, we already know what's working. Results compound over time as the algorithm learns. Month three typically outperforms month one significantly. We're not promising overnight miracles, but we're also not asking you to wait six months to see anything.",
  },
  {
    q: "\"I'm not in Florida. Can you still work with me?\"",
    a: "Yes. The system works anywhere. The strategy, the scripting, the campaign build, and the automation are all location-independent. We coordinate video coaching remotely or travel when needed. Your market is wherever your best customers are.",
  },
];

const finalChecks = [
  "No long-term contracts",
  "Your ad spend goes straight to Meta, not through us",
  "A team that has worked in your local market",
  "A clear plan built around your specific business and offer",
];

const CtaRow = ({ children, sub }) => (
  <Reveal delay={120}>
    <div className="mt-12">
      <GoldButton href={CALENDAR_URL} testid="section-cta">{children} <span aria-hidden>→</span></GoldButton>
      {sub && <p className="mt-3 text-sm text-parch">{sub}</p>}
    </div>
  </Reveal>
);

const HeroHeadline = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-headline">
    <div className="absolute inset-0 bg-gradient-to-b from-ink-900 via-ink-900 to-ink-800" />
    <div className="absolute top-1/4 -right-40 w-[44rem] h-[44rem] bg-gold/8 blur-[140px] rounded-full" />
    <PaperPlane className="hidden sm:block absolute right-[12%] top-[22%] w-10 h-10 -rotate-12 opacity-30 animate-float" />
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10 w-full pt-20">
      <Eyebrow className="animate-fade-up">Meta Ads & Brand Voice · Tampa Bay & Nationwide</Eyebrow>
      <h1 className="mt-6 font-display font-black tracking-tighter text-white leading-[0.95] text-6xl sm:text-7xl lg:text-[8.5rem]">
        <span className="block animate-fade-up" style={{ animationDelay: "80ms" }}>Clarify.</span>
        <span className="block animate-fade-up text-cream/90" style={{ animationDelay: "220ms" }}>Refine.</span>
        <span className="block animate-fade-up text-gold" style={{ animationDelay: "360ms" }}>Amplify.</span>
      </h1>
      <p className="mt-8 text-xl sm:text-2xl text-cream/80 max-w-2xl animate-fade-up" style={{ animationDelay: "480ms" }}>
        Your ads aren't the problem. Your message is. We fix that first, and then we run the ads.
      </p>
      <p className="mt-4 text-base sm:text-lg text-cream/50 max-w-2xl animate-fade-up" style={{ animationDelay: "560ms" }}>
        A done-for-you Meta ads system for local businesses in Tampa Bay and beyond, built around your voice and not a template.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "640ms" }}>
        <GoldButton href={CALENDAR_URL} testid="hero-apply-btn">Book a Free Strategy Call</GoldButton>
        <GhostButton to="/process" testid="hero-how-btn">See How It Works</GhostButton>
      </div>
    </div>
    <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-cream/40 pointer-events-none">
      <span className="label text-[10px]">Scroll</span>
      <ArrowDown size={18} className="animate-pulse-down text-gold" />
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Meta Ads & Brand Voice Agency | Bradenton, Sarasota & Tampa | Prolific Maven"
        description="Your ads didn't fail. Your message was never clarified. We fix that first, then run your Meta ads. Book a free strategy call with Prolific Maven."
        path="/"
        jsonLd={jsonLd}
      />

      <HeroHeadline />
      <VideoHero />

      {/* THE REAL REASON ADS FAIL */}
      <section className="relative z-10 bg-ink-900 py-24 sm:py-32" data-testid="honest-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>The thing nobody told you</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
              Your ads didn't fail. <span className="text-gold">You skipped two steps.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 text-lg text-parch leading-relaxed">
              Most business owners who've tried running ads and got nothing out of it made the same mistake. They paid to <em className="text-cream">amplify a message they never clarified or refined.</em> They skipped straight to spending money, without ever figuring out who they were really talking to or what those people actually needed to hear.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-lg text-parch leading-relaxed">
              It's not your fault. Every platform, every agency, and every "boost this post" button pushes you straight to spending. But there's a sequence that works. It starts long before the ads turn on.
            </p>
          </Reveal>

          <Reveal delay={160}>
            <div className="mt-10 bg-ink-800 border border-gold/20 p-8">
              <p className="font-display text-xl text-cream leading-snug">
                Ads are just a <span className="text-gold">delivery system.</span> If what you're delivering isn't clear, isn't compelling, and isn't aimed at the right person, it doesn't matter how much you spend. You're just paying to be ignored faster.
              </p>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <p className="mt-8 text-lg text-parch leading-relaxed">
              The three-step framework we use, <strong className="text-cream">Clarify, Refine, Amplify</strong>, exists because order matters. Every step has to happen before the next one. Here's exactly what each one means:
            </p>
          </Reveal>

          {/* CLARIFY / REFINE / AMPLIFY BREAKDOWN */}
          <div className="mt-12">
            {framework.map((f, i) => (
              <Reveal key={f.step} delay={i * 100} className="relative py-10 border-b border-ink-line">
                <span className="absolute -top-2 right-0 font-display text-7xl sm:text-8xl text-gold/8 leading-none pointer-events-none select-none">{f.step}</span>
                <div className="flex items-center gap-3 mb-3">
                  <span className="label text-[10px] text-gold">Step {f.step}</span>
                  <span className="label text-[10px] text-gold/50">·</span>
                  <span className="label text-[10px] text-gold">{f.label}</span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl text-cream relative leading-snug">{f.t}</h3>
                <p className="mt-4 text-parch leading-relaxed max-w-3xl relative">{f.d}</p>
              </Reveal>
            ))}
          </div>

          <CtaRow sub="Free 30-min call · No pitch · Just a plan">I'm Ready to Start at Step One</CtaRow>
        </div>
      </section>

      <InkDivider />

      {/* VILLAIN: WHY OTHER OPTIONS FAIL */}
      <section className="relative z-10 bg-ink-800 py-24 sm:py-32 border-y border-white/10" data-testid="villain-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>What you've probably already tried</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
              Here's why the other options <span className="text-gold">didn't get you customers.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-7 text-lg text-parch leading-relaxed">
              Business owners don't hate marketing. They hate spending money on things that don't produce customers. These are the three most common traps, with a plain explanation of what's actually happening when you use them.
            </p>
          </Reveal>

          <div className="mt-10 border-t border-ink-line">
            {villains.map((v, i) => (
              <Reveal key={i} delay={i * 80} className="flex gap-4 py-8 border-b border-ink-line">
                <X size={20} className="text-[#c0533a] shrink-0 mt-1" />
                <div>
                  <p className="font-display text-lg text-cream mb-2">{v.t}</p>
                  <p className="text-parch leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="mt-10 bg-ink-900 p-8 border border-ink-line">
              <p className="text-cream/85 leading-relaxed">
                None of these options are wrong because they're expensive. They're wrong because <strong className="text-gold">none of them are built around converting strangers into customers.</strong> They're built around visibility. And visibility without a clear, refined message is just noise.
              </p>
            </div>
          </Reveal>

          <CtaRow>Show Me What Actually Works</CtaRow>
        </div>
      </section>

      {/* BRIDGE */}
      <section className="relative z-10 bg-ink-900 py-24 sm:py-32 text-center" data-testid="bridge-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
              What if new clients found <em className="text-gold">you</em> instead of you chasing them?
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="w-12 h-px bg-gold mx-auto my-8" />
            <p className="text-lg text-parch leading-relaxed">
              Not hoping someone sees a post. Not waiting on a referral from a past client. Not competing on three lead platforms with five other businesses. People in your local market seeing your face, hearing your story, and reaching out already knowing they want to work with you.
            </p>
            <p className="mt-5 text-lg text-cream/90 leading-relaxed">
              That's what happens when you Clarify who you're talking to, Refine what you say to them, and Amplify it with a conversion-first ad system. It's not magic. It's sequence.
            </p>
          </Reveal>
          <CtaRow>Show Me the Full System</CtaRow>
        </div>
      </section>

      <InkDivider />

      {/* SYSTEM: SIX PHASES */}
      <section className="relative z-10 bg-ink-800 py-24 sm:py-32 border-y border-white/10" data-testid="system-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>How it works, step by step</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
              Six phases. Plain English. <span className="text-gold">No marketing jargon.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-7 text-lg text-parch leading-relaxed">
              Here's exactly what we do together, in the order we do it. The plain explanation comes first, and the technical details for anyone who wants to know exactly what's happening under the hood.
            </p>
          </Reveal>

          <div className="mt-12">
            {phases.map((p, i) => (
              <Reveal key={p.n} delay={i * 70} className="relative py-10 border-b border-ink-line">
                <span className="absolute -top-2 right-0 font-display text-7xl sm:text-8xl text-gold/8 leading-none pointer-events-none select-none">{p.n}</span>
                <span className="label text-[10px] text-gold">Phase {p.n}</span>
                <h3 className="mt-2 font-display text-xl sm:text-2xl text-cream relative leading-snug">{p.t}</h3>
                <p className="mt-4 text-parch leading-relaxed max-w-2xl relative">{p.d}</p>
              </Reveal>
            ))}
          </div>

          <CtaRow>Get This System Built for My Business</CtaRow>
        </div>
      </section>

      {/* FOR WHO */}
      <section className="relative z-10 bg-ink-900 py-24 sm:py-32" data-testid="forwho-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>Is this for you?</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
              This works for <span className="text-gold">one kind of business owner.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-7 text-lg text-parch leading-relaxed">
              Not every business. Not someone who's curious. This is for the owner who knows what they do is good, knows people need it, and is done being invisible while someone else gets the call.
            </p>
          </Reveal>

          <ul className="mt-10 border-t border-ink-line">
            {fits.map((f) => (
              <Reveal key={f} className="flex gap-4 py-5 border-b border-ink-line">
                <Check className="text-gold shrink-0 mt-1" size={20} />
                <span className="text-cream/85 leading-relaxed">{f}</span>
              </Reveal>
            ))}
          </ul>

          <Reveal delay={100} className="mt-10 bg-ink-800 border border-ink-line p-8">
            <h3 className="font-display text-xl text-cream">This is not the right fit if...</h3>
            <ul className="mt-5 space-y-3">
              {notFits.map((f) => (
                <li key={f} className="flex gap-3 text-cream/50 leading-relaxed"><X size={18} className="text-cream/25 shrink-0 mt-1" />{f}</li>
              ))}
            </ul>
          </Reveal>

          <CtaRow sub="Free 30-min call · No pitch · Just a plan">That's Me. Let's Talk</CtaRow>
        </div>
      </section>

      <InkDivider />

      {/* PACKAGES */}
      <Packages />

      {/* TESTIMONIALS */}
      <section className="relative z-10 bg-ink-800 py-24 sm:py-32 border-y border-white/10" data-testid="home-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <VideoTestimonials />
        </div>
      </section>

      {/* OBJECTIONS */}
      <section className="relative z-10 bg-ink-900 py-24 sm:py-32" data-testid="objections-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>Real questions, straight answers</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
              The questions worth asking <span className="text-gold">before you book a call.</span>
            </h2>
          </Reveal>

          <div className="mt-12 border-t border-ink-line">
            {objections.map((o, i) => (
              <Reveal key={i} delay={(i % 2) * 80} className="py-8 border-b border-ink-line">
                <p className="font-display italic text-xl text-cream">{o.q}</p>
                <p className="mt-4 text-parch leading-relaxed">{o.a}</p>
              </Reveal>
            ))}
          </div>

          <CtaRow sub="30 minutes · no pressure · no pitch">Still Have Questions? Bring Them to the Call</CtaRow>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 bg-ink-900 border-t border-white/10" data-testid="final-cta">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32">
          <div className="relative overflow-hidden border border-gold/30 bg-gradient-to-br from-ink-800 to-ink-900 p-10 sm:p-16 lg:p-20 text-center">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative max-w-3xl mx-auto">
              <Reveal>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
                  A year from now, someone in your market will own it. <span className="text-gold">It should be you.</span>
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="mt-6 text-lg text-parch leading-relaxed">
                  The business owners who start clarifying their message, refining how they deliver it, and amplifying it consistently are the ones who become the obvious choice in their market. That gap between you and them is one conversation.
                </p>
              </Reveal>
              <Reveal delay={150}>
                <ul className="mt-10 max-w-md mx-auto text-left">
                  {finalChecks.map((c) => (
                    <li key={c} className="flex gap-3 py-2.5 border-b border-ink-line text-cream/75"><Check size={18} className="text-gold shrink-0 mt-1" />{c}</li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={200}>
                <div className="mt-10">
                  <GoldButton href={CALENDAR_URL} testid="final-apply">Book a Free Strategy Call <span aria-hidden>→</span></GoldButton>
                </div>
                <p className="mt-4 text-sm text-parch/70">We keep the client roster small so every client gets real attention.</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
